import { forwardRef, ComponentPropsWithoutRef } from "react";

import styles from "./Link.module.scss";
import { Icon } from "../../Icon";
import OpenInNew from "@servicetitan/hammer-icon/mdi/round/open_in_new.svg";

/**
 * Props for the LinkHeadless component.
 * @extends ComponentPropsWithoutRef<"a">
 */
export type LinkHeadlessProps = ComponentPropsWithoutRef<"a">;

/**
 * Headless link component for creating accessible hyperlinks.
 *
 * Features:
 * - Security attributes (rel="noopener noreferrer") for external links
 *
 * @example
 * <LinkHeadless href="/dashboard">
 *   Go to Dashboard
 * </LinkHeadless>
 *
 * @example
 * <LinkHeadless href="https://example.com" target="_blank">
 *   External Documentation
 * </LinkHeadless>
 *
 */
export const LinkHeadless = forwardRef<HTMLAnchorElement, LinkHeadlessProps>(
  ({ children, ...props }, ref) => {
    return (
      <a
        rel="noopener noreferrer"
        data-anv="link-headless"
        ref={ref}
        {...props}
      >
        {children}
        {props.target === "_blank" && (
          <Icon className={styles["open-in-new"]} inherit svg={OpenInNew} />
        )}
      </a>
    );
  },
);

LinkHeadless.displayName = "LinkHeadless";
